import React, { useEffect, useRef, useState } from 'react';
import { CognitiveFilterRequestModel, ReportSearchModel, UiConfigurationModel, ReportCardType } from '@/types';
import { searchReports, searchMedia } from '@/api';
import { NeedPanelEvent } from '@egjs/react-flicking';
import { DIRECTION } from '@egjs/flicking';
import ReportThreeCardsClickCarousel from './ReportThreeCardsClickCarousel';
import ReportTwoRowsDragCarousel from './ReportTwoRowsDragCarousel';
import ReportSingleRowDragCarousel from './ReportSingleRowDragCarousel';
import ReportListingGrid from './ReportListingGrid';
import { ReportHeadingModel } from './ReportCarouselHeading';

type ReportsGridModel = {
  uiConfiguration?: UiConfigurationModel;
  requestFilter?: CognitiveFilterRequestModel;
  cards: ReportSearchModel[];
  cardType: ReportCardType;
  heading?: ReportHeadingModel;
  searchFunction?: (filterRequest: CognitiveFilterRequestModel) => Promise<any>;
  onCardClick?: (e: React.MouseEvent, data: ReportSearchModel) => void;
};

const ReportsGrid = (data: ReportsGridModel) => {
  const {
    requestFilter,
    uiConfiguration = { pageSize: 1, limit: 1, hideViewMore: false, layout: 'singleRowDrag' },
    cards,
    cardType,
    heading,
    searchFunction,
    onCardClick,
  } = data;
  const [noMoreDataToLoad, setNoMoreDataToLoad] = useState<boolean>(false);
  const currentCardPage = useRef<number>(0);

  const getSkeletons = (numberOfSkeletons: number) => {
    const skeletons: ReportSearchModel[] = [];
    for (let index = 0; index < numberOfSkeletons; index++) {
      skeletons.push({ isLoading: true } as ReportSearchModel);
    }
    return skeletons;
  };

  const [internalCardArray, setInternalCardArray] = useState<ReportSearchModel[]>(
    !cards || cards.length === 0 ? getSkeletons(uiConfiguration.pageSize) : cards.slice(0, uiConfiguration.pageSize),
  );

  useEffect(() => {
    if (!cards || cards.length === 0) {
      getData();
      currentCardPage.current = currentCardPage.current + 1;
    } else if (cards && cards.length >= uiConfiguration.limit) {
      setNoMoreDataToLoad(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getData = async () => {
    const realDataLength = internalCardArray.filter((item) => !item.isLoading).length;
    let numberOfItemsToAdd = uiConfiguration.pageSize;
    if (uiConfiguration.limit && realDataLength + numberOfItemsToAdd > uiConfiguration.limit) {
      numberOfItemsToAdd = uiConfiguration.limit - internalCardArray.length;
    }

    const filterRequest: CognitiveFilterRequestModel = {
      ...requestFilter,
      page: currentCardPage.current,
      pageSize: uiConfiguration.pageSize,
    };

    const dataResponse = await (searchFunction
      ? searchFunction(filterRequest)
      : cardType === 'VideoCardModel'
        ? searchMedia(filterRequest)
        : searchReports(filterRequest));

    setInternalCardArray([
      ...internalCardArray.filter((item) => !item.isLoading),
      ...dataResponse.results.slice(0, numberOfItemsToAdd),
    ]);

    if (
      dataResponse.results.length < uiConfiguration.pageSize ||
      (uiConfiguration.limit && internalCardArray.length >= uiConfiguration.limit)
    ) {
      setNoMoreDataToLoad(true);
    }
  };

  const needPanel = ({ direction }: NeedPanelEvent) => {
    if (
      direction === DIRECTION.NEXT &&
      !noMoreDataToLoad &&
      internalCardArray.filter((item) => !item.isLoading).length < uiConfiguration.limit
    ) {
      setInternalCardArray([...internalCardArray, ...getSkeletons(uiConfiguration.pageSize)]);
      currentCardPage.current = currentCardPage.current + 1;

      getData();
    }
  };

  if (uiConfiguration.layout === 'listingGrid') {
    return (
      <ReportListingGrid
        cards={internalCardArray}
        cardType={cardType}
        heading={heading}
        uiConfiguration={uiConfiguration}
        onCardClick={onCardClick}
      />
    );
  } else if (uiConfiguration.layout === 'threeCardsClick') {
    return (
      <ReportThreeCardsClickCarousel
        cards={internalCardArray}
        cardType={cardType}
        onNeedPanel={needPanel}
        heading={heading}
        uiConfiguration={uiConfiguration}
        onCardClick={onCardClick}
      />
    );
  } else if (uiConfiguration.layout === 'twoRowsDrag') {
    return (
      <ReportTwoRowsDragCarousel
        cards={internalCardArray}
        cardType={cardType}
        onNeedPanel={needPanel}
        heading={heading}
        uiConfiguration={uiConfiguration}
        onCardClick={onCardClick}
      />
    );
  } else {
    return (
      <ReportSingleRowDragCarousel
        cards={internalCardArray}
        cardType={cardType}
        onNeedPanel={needPanel}
        heading={heading}
        uiConfiguration={uiConfiguration}
        onCardClick={onCardClick}
      />
    );
  }
};

export default ReportsGrid;
